import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { clientBasicInfoSchema, ClientBasicInfo } from '@/lib/clientValidation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface ClientBasicInfoTabProps {
  defaultValues: ClientBasicInfo;
  onSave: (data: ClientBasicInfo) => Promise<void>;
  isSaving: boolean;
}

export default function ClientBasicInfoTab({
  defaultValues,
  onSave,
  isSaving,
}: ClientBasicInfoTabProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<ClientBasicInfo>({
    resolver: zodResolver(clientBasicInfoSchema),
    defaultValues,
  });

  const clientStatus = watch('client_status');
  const preferredContact = watch('preferred_contact_method');

  return (
    <form onSubmit={handleSubmit(onSave)} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="full_name">Full Name *</Label>
          <Input
            id="full_name"
            {...register('full_name')}
            placeholder="John Doe"
          />
          {errors.full_name && (
            <p className="text-sm text-destructive mt-1">{errors.full_name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            placeholder="john@example.com"
          />
          {errors.email && (
            <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            {...register('phone')}
            placeholder="555-1234"
          />
          {errors.phone && (
            <p className="text-sm text-destructive mt-1">{errors.phone.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="client_status">Status *</Label>
          <Select value={clientStatus} onValueChange={(value) => setValue('client_status', value as any, { shouldDirty: true })}>
            <SelectTrigger id="client_status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          {errors.client_status && (
            <p className="text-sm text-destructive mt-1">{errors.client_status.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="preferred_contact_method">Preferred Contact Method</Label>
          <Select value={preferredContact || ''} onValueChange={(value) => setValue('preferred_contact_method', value as any, { shouldDirty: true })}>
            <SelectTrigger id="preferred_contact_method">
              <SelectValue placeholder="Select method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="text">Text</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3 pt-4 border-t">
          <h4 className="font-medium">Address</h4>
          
          <div>
            <Label htmlFor="address_street">Street</Label>
            <Input
              id="address_street"
              {...register('address.street')}
              placeholder="123 Main St"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="address_city">City</Label>
              <Input
                id="address_city"
                {...register('address.city')}
                placeholder="Anytown"
              />
            </div>
            <div>
              <Label htmlFor="address_state">State</Label>
              <Input
                id="address_state"
                {...register('address.state')}
                placeholder="FL"
                maxLength={2}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="address_zip">ZIP Code</Label>
            <Input
              id="address_zip"
              {...register('address.zip')}
              placeholder="12345"
              maxLength={10}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button
          type="submit"
          disabled={isSaving || !isDirty}
        >
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Changes
        </Button>
      </div>
    </form>
  );
}
